// context/TranslationProvider.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';

// Define the supported languages
export type Language = 'en' | 'ar' | 'dv';

// Define the translation structure
interface Translations {
  [key: string]: string | Translations;
}

// Define the context type
interface TranslationContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  direction?: 'ltr' | 'rtl';
  language: Language;
  setLanguage: (lang: Language) => void;
  availableLanguages: { code: Language; name: string; dir: 'ltr' | 'rtl' }[];
}

// Create context
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

// Language configuration
const languages: { code: Language; name: string; dir: 'ltr' | 'rtl' }[] = [
  { code: 'en', name: 'English', dir: 'ltr' },
  { code: 'ar', name: 'العربية', dir: 'rtl' },
  { code: 'dv', name: 'ދިވެހި', dir: 'rtl' },
];

// Import translations dynamically
const loadTranslations = async (lang: Language): Promise<Translations> => {
  try {
    const module = await import(`../locales/${lang}.json`);
    return module.default;
  } catch (error) {
    console.error(`Failed to load translations for ${lang}:`, error);
    // Fallback to English if translation fails
    const fallbackModule = await import(`../locales/en.json`);
    return fallbackModule.default;
  }
};

// Helper function to get nested object value by dot notation
const getNestedValue = (obj: Translations, path: string): string | undefined => {
  const keys = path.split('.');
  let current: any = obj;
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return undefined;
    }
  }
  
  return typeof current === 'string' ? current : undefined;
};

// Helper function to interpolate parameters
const interpolate = (text: string, params?: Record<string, string | number>): string => {
  if (!params) return text;
  
  return text.replace(/{(\w+)}/g, (match, key) => {
    return params[key] !== undefined ? String(params[key]) : match;
  });
};

// Provider component
interface TranslationProviderProps {
  children: React.ReactNode;
  defaultLanguage?: Language;
}

export const TranslationProvider: React.FC<TranslationProviderProps> = ({ 
  children, 
  defaultLanguage = 'en' 
}) => {
  const [language, setLanguage] = useState<Language>(defaultLanguage);
  const [translations, setTranslations] = useState<Translations>({});
  const [isLoading, setIsLoading] = useState(true);


  // Load translations when language changes
  useEffect(() => {
    const loadLanguage = async () => {
      setIsLoading(true);
      const loadedTranslations = await loadTranslations(language);
      setTranslations(loadedTranslations);
      setIsLoading(false);
      
      // Update document direction for RTL/LTR
      const direction = languages.find(lang => lang.code === language)?.dir || 'ltr';
      document.documentElement.dir = direction;
      document.documentElement.lang = language;
      
      // Update meta tag for direction
      const metaDir = document.querySelector('meta[name="viewport"]');
      if (metaDir) {
        metaDir.setAttribute('content', `width=device-width, initial-scale=1.0, user-scalable=yes`);
      }
      
      // Add class to body for RTL styling if needed
      if (direction === 'rtl') {
        document.body.classList.add('rtl');
        document.body.classList.remove('ltr');
      } else {
        document.body.classList.add('ltr');
        document.body.classList.remove('rtl');
      }
    };
    
    loadLanguage();
  }, [language]);

  // Translation function
  const t = (key: string, params?: Record<string, string | number>): string => {
    if (isLoading) {
      // Return the key while loading to avoid empty text
      return key;
    }
    
    const value = getNestedValue(translations, key);
    
    if (!value) {
      console.warn(`Translation missing for key: ${key} in language: ${language}`);
      return key;
    }
    
    return interpolate(value, params);
  };

  const value: TranslationContextType = {
    t,
    direction: languages.find(lang => lang.code === language)?.dir || 'ltr',
    language,
    setLanguage,
    availableLanguages: languages,
  };

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

// Custom hook to use translation
export const useLang = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useLang must be used within a TranslationProvider');
  }
  return context;
};

// Optional: Higher-order component for class components
export const withTranslation = <P extends object>(
  Component: React.ComponentType<P & { t: TranslationContextType['t']; language: Language; setLanguage: TranslationContextType['setLanguage'] }>
) => {
  return (props: P) => {
    const { t, language, setLanguage } = useLang();
    return <Component {...props} t={t} language={language} setLanguage={setLanguage} />;
  };
};